import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ShoppingCart, Package, Shield, Clock, AlertCircle, Check, ChevronLeft, ChevronRight, Star, MessageSquare, ZoomIn, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Seo from "@/components/Seo";
import ProductDetailFooter, { pushRecentlyViewed } from "@/components/ProductDetailFooter";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import NotifyMeButton from "@/components/NotifyMeButton";
import ProductImage from "@/components/ProductImage";
import WishlistButton from "@/components/WishlistButton";

const API = "/api";

function ImageLightbox({ images, currentIndex, onClose, onPrev, onNext }) {
  var [zoom, setZoom] = useState(1);
  var [position, setPosition] = useState({ x: 0, y: 0 });
  var [isDragging, setIsDragging] = useState(false);
  var [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  function handleZoomIn() {
    setZoom(function(z) { return Math.min(3, z + 0.5); });
  }

  function handleZoomOut() {
    setZoom(function(z) { 
      var newZoom = Math.max(1, z - 0.5);
      if (newZoom === 1) setPosition({ x: 0, y: 0 });
      return newZoom;
    });
  }

  function handleMouseDown(e) {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  }

  function handleMouseMove(e) {
    if (isDragging && zoom > 1) {
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  }

  function handleMouseUp() {
    setIsDragging(false);
  }

  function handleKeyDown(e) {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowLeft") { setZoom(1); setPosition({ x: 0, y: 0 }); onPrev(); }
    if (e.key === "ArrowRight") { setZoom(1); setPosition({ x: 0, y: 0 }); onNext(); }
    if (e.key === "+" || e.key === "=") handleZoomIn();
    if (e.key === "-") handleZoomOut();
  }

  useEffect(function() {
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return function() {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [zoom]);

  function resetAndNav(fn) {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    fn();
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
        onClick={function(e) { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {/* Zoom controls */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-black/50 rounded-full px-4 py-2">
          <button onClick={handleZoomOut} disabled={zoom <= 1} className="p-2 text-white hover:text-[#C0A062] disabled:opacity-30 disabled:cursor-not-allowed">
            <span className="text-lg font-bold">−</span>
          </button>
          <span className="text-white font-mono text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={handleZoomIn} disabled={zoom >= 3} className="p-2 text-white hover:text-[#C0A062] disabled:opacity-30 disabled:cursor-not-allowed">
            <span className="text-lg font-bold">+</span>
          </button>
        </div>

        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={function() { resetAndNav(onPrev); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>
            <button
              onClick={function() { resetAndNav(onNext); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </button>
          </>
        )}

        {/* Main image */}
        <div
          className="relative max-w-[90vw] max-h-[85vh] overflow-hidden cursor-move"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <motion.img
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            src={images[currentIndex]}
            alt=""
            className="max-w-[90vw] max-h-[85vh] object-contain select-none"
            style={{
              transform: "scale(" + zoom + ") translate(" + (position.x / zoom) + "px, " + (position.y / zoom) + "px)",
              cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "zoom-in"
            }}
            onClick={function() { if (zoom === 1) handleZoomIn(); }}
            draggable={false}
          />
        </div>

        {/* Image counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 bg-black/50 rounded-full px-4 py-2">
          <span className="text-white font-mono text-sm">{currentIndex + 1} / {images.length}</span>
        </div>

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-50 flex gap-2 bg-black/50 rounded-lg p-2 max-w-[80vw] overflow-x-auto">
            {images.map(function(img, idx) {
              var isActive = idx === currentIndex;
              return (
                <button
                  key={idx}
                  onClick={function() { resetAndNav(function() { /* handled by parent */ }); }}
                  className={"w-16 h-16 rounded overflow-hidden border-2 transition-all flex-shrink-0 " + (isActive ? "border-[#C0A062] opacity-100" : "border-transparent opacity-60 hover:opacity-100")}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              );
            })}
          </div>
        )}

        {/* Instructions */}
        <div className="absolute bottom-4 right-4 z-50 text-[#71717a] text-xs font-mono hidden md:block">
          <p>Click to zoom • Drag to pan • Arrow keys to navigate • ESC to close</p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function ProductDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [userOrders, setUserOrders] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewComment, setReviewComment] = useState("");
  const [reviewRating, setReviewRating] = useState("5");
  const [selectedOrder, setSelectedOrder] = useState("");
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState({});  // { addonId: quantity }

  useEffect(function() {
    axios.get(API + "/products/" + params.productId)
      .then(function(res) {
        setProduct(res.data);
        pushRecentlyViewed(res.data.id);
      })
      .catch(function() { toast.error("Product not found"); navigate("/shop"); })
      .finally(function() { setIsLoading(false); });
    
    // Fetch reviews
    axios.get(API + "/reviews/product/" + params.productId)
      .then(function(res) { 
        setReviews(res.data.reviews || []); 
        setAvgRating(res.data.average_rating || 0);
      })
      .catch(function() {});
  }, [params.productId, navigate]);

  useEffect(function() {
    // Fetch user's orders for review verification
    if (user) {
      axios.get(API + "/orders/user/" + user.email)
        .then(function(res) {
          // Filter orders that contain this product
          var ordersWithProduct = res.data.filter(function(order) {
            return order.payment_status === "completed" && 
              order.items.some(function(item) { return item.product_id === params.productId; });
          });
          setUserOrders(ordersWithProduct);
        })
        .catch(function() {});
    }
  }, [user, params.productId]);

  function handleSubmitReview(e) {
    e.preventDefault();
    if (!selectedOrder) {
      toast.error("Please select an order to verify your purchase");
      return;
    }
    setIsSubmittingReview(true);
    axios.post(API + "/reviews", {
      product_id: params.productId,
      product_name: product.name,
      order_id: selectedOrder,
      rating: parseInt(reviewRating),
      title: reviewTitle,
      comment: reviewComment
    })
      .then(function() {
        toast.success("Review submitted! Thank you for your feedback.");
        setShowReviewForm(false);
        setReviewTitle("");
        setReviewComment("");
        setReviewRating("5");
        setSelectedOrder("");
        // Refresh reviews
        axios.get(API + "/reviews/product/" + params.productId)
          .then(function(res) { 
            setReviews(res.data.reviews || []); 
            setAvgRating(res.data.average_rating || 0);
          });
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || "Failed to submit review");
      })
      .finally(function() { setIsSubmittingReview(false); });
  }

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C0A062] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) return null;

  var images = product.images || [];
  var hasImages = images.length > 0;
  var imageCount = images.length;
  var currentImage = hasImages ? images[selectedImage] : null;
  var hasSale = product.sale_price && product.sale_price < product.price;
  var displayPrice = hasSale ? product.sale_price : product.price;

  function formatPrice(p) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(p);
  }

  // Calculate total add-on price
  function getAddonTotal() {
    if (!product.addons) return 0;
    var total = 0;
    product.addons.forEach(function(addon) {
      var qty = selectedAddons[addon.id] || 0;
      total += addon.price * qty;
    });
    return total;
  }

  function handleAddonChange(addonId, newQty) {
    setSelectedAddons(function(prev) {
      var updated = { ...prev };
      if (newQty <= 0) {
        delete updated[addonId];
      } else {
        updated[addonId] = newQty;
      }
      return updated;
    });
  }

  function handleAddToCart() {
    if (product.in_stock) {
      // Prepare add-ons data for cart
      var addonsForCart = [];
      if (product.addons) {
        product.addons.forEach(function(addon) {
          var qty = selectedAddons[addon.id] || 0;
          if (qty > 0) {
            addonsForCart.push({ id: addon.id, name: addon.name, price: addon.price, quantity: qty });
          }
        });
      }
      cart.addToCart(product, quantity, addonsForCart);
      setAddedToCart(true);
      toast.success(product.name + " added to cart!");
      setTimeout(function() { setAddedToCart(false); }, 2000);
    }
  }

  function handleBuyNow() {
    if (product.in_stock) {
      var addonsForCart = [];
      if (product.addons) {
        product.addons.forEach(function(addon) {
          var qty = selectedAddons[addon.id] || 0;
          if (qty > 0) {
            addonsForCart.push({ id: addon.id, name: addon.name, price: addon.price, quantity: qty });
          }
        });
      }
      cart.addToCart(product, quantity, addonsForCart);
      cart.setIsCartOpen(true);
    }
  }

  function prevImage() {
    setSelectedImage(function(i) { return (i - 1 + imageCount) % imageCount; });
  }

  function nextImage() {
    setSelectedImage(function(i) { return (i + 1) % imageCount; });
  }

  function decreaseQty() {
    setQuantity(function(q) { return Math.max(1, q - 1); });
  }

  function increaseQty() {
    setQuantity(function(q) { return q + 1; });
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="product-detail-page">
      <Seo
        title={product.name}
        description={(product.description || "").slice(0, 200) || ("Quality reproduction part: " + product.name)}
        path={"/product/" + product.id}
        image={hasImages ? product.images[0] : undefined}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "description": (product.description || "").slice(0, 500),
          "image": product.images || [],
          "sku": product.id,
          "category": product.category,
          "brand": { "@type": "Brand", "name": "Resurrected Gun Parts" },
          "offers": {
            "@type": "Offer",
            "url": "https://resurrectedgunparts.com/product/" + product.id,
            "priceCurrency": "USD",
            "price": (product.sale_price || product.price || 0).toFixed(2),
            "availability": product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "seller": { "@type": "Organization", "name": "Resurrected Gun Parts" }
          }
        }}
      />
      <div className="container-tactical">
        <Link to="/shop" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#C0A062] mb-8" data-testid="back-to-shop">
          <ArrowLeft className="w-4 h-4" /><span className="font-mono text-sm uppercase">Back to Shop</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
            <div 
              className="relative aspect-square bg-[#121212] border border-white/5 rounded-sm overflow-hidden group cursor-zoom-in"
              onClick={function() { if (hasImages) setShowLightbox(true); }}
              data-testid="product-image-main"
            >
              {currentImage ? (
                <ProductImage 
                  src={currentImage} 
                  alt={product.name} 
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
                  retryCount={3}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Package className="w-24 h-24 text-[#3f3f46]" /></div>
              )}
              {/* Zoom overlay on hover */}
              {hasImages && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center pointer-events-none">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-3">
                    <ZoomIn className="w-8 h-8 text-white" />
                  </div>
                </div>
              )}
              {imageCount > 1 && (
                <>
                  <button onClick={function(e) { e.stopPropagation(); prevImage(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-sm flex items-center justify-center z-10">
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  <button onClick={function(e) { e.stopPropagation(); nextImage(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-sm flex items-center justify-center z-10">
                    <ChevronRight className="w-5 h-5 text-white" />
                  </button>
                </>
              )}
              <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
                {hasSale && <span className="px-3 py-1 bg-[#C0A062] text-black text-sm font-mono uppercase">Sale</span>}
                {!product.in_stock && <span className="px-3 py-1 bg-red-500 text-white text-sm font-mono uppercase">Sold Out</span>}
              </div>
              {/* Click to zoom hint */}
              {hasImages && (
                <div className="absolute bottom-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-white text-xs font-mono bg-black/60 px-2 py-1 rounded">Click to zoom</span>
                </div>
              )}
            </div>
            {imageCount > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {images.map(function(img, idx) {
                  var isActive = selectedImage === idx;
                  var borderClass = isActive ? "border-[#C0A062]" : "border-white/10 hover:border-white/30";
                  return (
                    <button key={idx} onClick={function() { setSelectedImage(idx); }} className={"w-20 h-20 rounded-sm overflow-hidden border-2 transition-colors flex-shrink-0 " + borderClass}>
                      <ProductImage src={img} alt="" className="w-full h-full object-cover" retryCount={2} showFallbackIcon={false} />
                    </button>
                  );
                })}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
            <span className="category-badge">{product.category}</span>
            <h1 className="font-heading font-black text-4xl md:text-5xl text-white uppercase tracking-tight">{product.name}</h1>
            <div className="flex items-center gap-4">
              <span className="font-heading font-bold text-4xl text-[#C0A062]">{formatPrice(displayPrice)}</span>
              {hasSale && <span className="font-heading text-2xl text-[#71717a] line-through">{formatPrice(product.price)}</span>}
            </div>
            {product.in_stock ? (
              <div className="flex items-center gap-2 text-[#8CB369]"><Check className="w-5 h-5" /><span className="font-mono text-sm uppercase">In Stock</span></div>
            ) : (
              <div className="flex items-center gap-2 text-red-400"><AlertCircle className="w-5 h-5" /><span className="font-mono text-sm uppercase">Sold Out</span></div>
            )}
            <p className="text-[#A1A1AA] text-lg leading-relaxed">{product.description}</p>
            {product.in_stock ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <span className="text-[#A1A1AA] font-mono text-sm uppercase">Qty</span>
                  <div className="flex items-center border border-white/10 rounded-sm">
                    <button onClick={decreaseQty} className="px-4 py-2 text-white hover:bg-white/5">-</button>
                    <span className="px-4 py-2 text-white font-mono border-x border-white/10">{quantity}</span>
                    <button onClick={increaseQty} className="px-4 py-2 text-white hover:bg-white/5">+</button>
                  </div>
                </div>
                
                {/* Product Add-ons */}
                {product.addons && product.addons.length > 0 && (
                  <div className="p-4 bg-[#1a1a1a] border border-[#C0A062]/30 rounded-sm space-y-3">
                    <p className="font-mono text-xs text-[#C0A062] uppercase tracking-wider">Optional Add-ons</p>
                    {product.addons.map(function(addon) {
                      var addonQty = selectedAddons[addon.id] || 0;
                      return (
                        <div key={addon.id} className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <p className="text-white font-medium">{addon.name}</p>
                            <p className="text-[#C0A062] text-sm">+{formatPrice(addon.price)} each</p>
                          </div>
                          <div className="flex items-center border border-white/10 rounded-sm">
                            <button 
                              onClick={function() { handleAddonChange(addon.id, addonQty - 1); }}
                              className="px-3 py-1 text-white hover:bg-white/5 text-sm"
                            >-</button>
                            <span className="px-3 py-1 text-white font-mono text-sm border-x border-white/10 min-w-[40px] text-center">{addonQty}</span>
                            <button 
                              onClick={function() { 
                                var max = addon.max_quantity || 99;
                                if (addonQty < max) handleAddonChange(addon.id, addonQty + 1); 
                              }}
                              className="px-3 py-1 text-white hover:bg-white/5 text-sm"
                            >+</button>
                          </div>
                        </div>
                      );
                    })}
                    {getAddonTotal() > 0 && (
                      <div className="pt-2 border-t border-white/10 flex justify-between">
                        <span className="text-[#A1A1AA] text-sm">Add-ons total:</span>
                        <span className="text-[#C0A062] font-mono">{formatPrice(getAddonTotal())}</span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex gap-4">
                  <Button className="btn-primary flex-1" onClick={handleAddToCart} disabled={addedToCart} data-testid="add-to-cart-detail">
                    {addedToCart ? <><Check className="w-4 h-4 mr-2" />Added!</> : <><ShoppingCart className="w-4 h-4 mr-2" />Add to Cart</>}
                  </Button>
                  <Button className="btn-secondary flex-1" onClick={handleBuyNow} data-testid="buy-now">Buy Now</Button>
                </div>
                <WishlistButton productId={product.id} variant="full" />
              </div>
            ) : (
              <div className="space-y-4">
                <NotifyMeButton productId={product.id} productName={product.name} />
                <WishlistButton productId={product.id} variant="full" />
              </div>
            )}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
              <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#C0A062]" /><span className="text-sm text-[#A1A1AA]">Quality</span></div>
              <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-[#C0A062]" /><span className="text-sm text-[#A1A1AA]">Made to Order</span></div>
              <div className="flex items-center gap-2"><Package className="w-5 h-5 text-[#C0A062]" /><span className="text-sm text-[#A1A1AA]">Secure Ship</span></div>
            </div>
            <div className="p-4 bg-[#121212] border border-white/5 rounded-sm">
              <p className="font-mono text-xs text-[#C0A062] uppercase mb-2">Payment Options</p>
              <p className="text-sm text-[#A1A1AA]">PayPal & Venmo accepted at checkout.</p>
            </div>
          </motion.div>
        </div>

        {/* Reviews Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.3 }}
          className="mt-16 pt-16 border-t border-white/10"
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading font-bold text-2xl text-white uppercase">Customer Reviews</h2>
              {reviews.length > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[#C0A062] text-lg">{"★".repeat(Math.round(avgRating))}{"☆".repeat(5-Math.round(avgRating))}</span>
                  <span className="text-[#A1A1AA]">{avgRating.toFixed(1)} out of 5 ({reviews.length} reviews)</span>
                </div>
              )}
            </div>
            {user && userOrders.length > 0 && !showReviewForm && (
              <Button className="btn-secondary" onClick={function() { setShowReviewForm(true); }}>
                <MessageSquare className="w-4 h-4 mr-2" />Write a Review
              </Button>
            )}
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-8">
              <h3 className="font-heading font-bold text-white text-lg mb-4">Write Your Review</h3>
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Verify Purchase *</Label>
                  <Select value={selectedOrder} onValueChange={setSelectedOrder}>
                    <SelectTrigger className="input-tactical"><SelectValue placeholder="Select your order" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      {userOrders.map(function(order) {
                        return <SelectItem key={order.order_id} value={order.order_id}>Order #{order.order_id.slice(0,8)}...</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Rating *</Label>
                  <Select value={reviewRating} onValueChange={setReviewRating}>
                    <SelectTrigger className="input-tactical w-40"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      <SelectItem value="5">★★★★★ (5)</SelectItem>
                      <SelectItem value="4">★★★★☆ (4)</SelectItem>
                      <SelectItem value="3">★★★☆☆ (3)</SelectItem>
                      <SelectItem value="2">★★☆☆☆ (2)</SelectItem>
                      <SelectItem value="1">★☆☆☆☆ (1)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Title *</Label>
                  <Input value={reviewTitle} onChange={function(e) { setReviewTitle(e.target.value); }} required className="input-tactical" placeholder="Summarize your experience" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Your Review *</Label>
                  <Textarea value={reviewComment} onChange={function(e) { setReviewComment(e.target.value); }} required className="input-tactical min-h-[100px]" placeholder="Share your thoughts about this product..." />
                </div>
                <div className="flex gap-4">
                  <Button type="button" onClick={function() { setShowReviewForm(false); }} className="btn-secondary">Cancel</Button>
                  <Button type="submit" className="btn-primary" disabled={isSubmittingReview}>
                    {isSubmittingReview ? "Submitting..." : "Submit Review"}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Reviews List */}
          {reviews.length > 0 ? (
            <div className="space-y-6">
              {reviews.map(function(review) {
                return (
                  <div key={review.id} className="bg-[#121212] border border-white/5 rounded-sm p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[#C0A062]">{"★".repeat(review.rating)}{"☆".repeat(5-review.rating)}</span>
                          <span className="text-white font-bold">{review.title}</span>
                        </div>
                        <p className="text-[#71717a] text-sm">By {review.user_name} - Verified Purchase</p>
                      </div>
                      <span className="text-[#71717a] text-xs">{new Date(review.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-[#A1A1AA]">{review.comment}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#121212] border border-white/5 rounded-sm">
              <Star className="w-12 h-12 text-[#3f3f46] mx-auto mb-4" />
              <p className="text-[#71717a]">No reviews yet. Be the first to review this product!</p>
              {!user && <p className="text-[#71717a] text-sm mt-2">Sign in and purchase this product to leave a review.</p>}
            </div>
          )}
        </motion.div>
      </div>

      <ProductDetailFooter product={product} />

      {/* Image Lightbox */}
      {showLightbox && hasImages && (
        <ImageLightbox
          images={images}
          currentIndex={selectedImage}
          onClose={function() { setShowLightbox(false); }}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </div>
  );
}
